import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Search, PhoneCall, ArrowRight, CheckCircle2, Shield, Globe, Lock, Activity, Star, Brain, Zap, Trophy } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { WarningBanner } from '../components/WarningBanner';
import { ProtocolCard } from '../components/ProtocolCard';
import { scamProtocols } from '../data/scamProtocols';
import { SEO } from '../components/SEO';
export function HomePage() {
  const {
    language,
    t
  } = useLanguage();
  return <div className="flex flex-col min-h-screen bg-slate-50 overflow-x-hidden" data-id="element-852">
      <SEO title={t('seo.home.title')} description={t('seo.home.desc')} data-id="element-853" />

      {/* Hero Section - Elegant American Navy */}
      <section className="relative min-h-[85vh] flex items-center justify-center pt-24 pb-16 overflow-hidden bg-primary-900" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/e7LiWRk7qigWqU2XwRR7Mj/markus-spiske-TaKB-4F58ek-unsplash.jpg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} data-id="element-854">
        {/* Dark Overlay for Text Readability */}
        <div className="absolute inset-0 bg-primary-900/80 mix-blend-multiply" data-id="element-855"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/60 via-primary-900/40 to-primary-900" data-id="element-856"></div>

        {/* American Star Pattern Overlay */}
        <div className="absolute inset-0 pattern-stars opacity-10" data-id="element-857"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-858">
          <div className="max-w-4xl mx-auto text-center" data-id="element-859">
            {/* Elegant Badge */}
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary-800/80 border border-primary-600 text-sm font-medium text-white mb-8 animate-fade-in shadow-lg backdrop-blur-sm" data-id="element-860">
              <span className="flex h-2 w-2 rounded-full bg-accent-500 mr-2 shadow-[0_0_8px_rgba(178,34,52,0.6)]" data-id="element-861"></span>
              {t('home.badge')}
            </div>

            {/* Main Heading - Clean White */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight text-white animate-slide-up drop-shadow-lg" data-id="element-862">
              {t('hero.title')}
            </h1>

            <p className="text-xl sm:text-2xl text-primary-100 mb-2 font-medium animate-slide-up delay-100" data-id="element-863">
              {t('hero.subtitle')}
            </p>

            <p className="text-lg text-primary-200 mb-10 max-w-2xl mx-auto font-light leading-relaxed animate-slide-up delay-100" data-id="element-864">
              {t('home.heroDesc')}
            </p>

            {/* CTA Buttons - Minimal & Professional */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up delay-200" data-id="element-865">
              <Button href={`/${language}/emergency-protocol`} variant="destructive" size="xl" className="w-full sm:w-auto shadow-lg shadow-destructive/20" leftIcon={<Activity className="w-5 h-5" data-id="element-867" />} data-id="element-866">
                {t('hero.cta.emergency')}
              </Button>
              <Button href={`/${language}/check-scam`} variant="secondary" size="xl" className="w-full sm:w-auto shadow-lg" leftIcon={<Search className="w-5 h-5" data-id="element-869" />} data-id="element-868">
                {t('hero.cta.check')}
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Gradient Line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-900 via-accent-600 to-primary-900" data-id="element-870"></div>
      </section>

      {/* Global Security Warning - Enhanced Design */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 -mt-12 sm:-mt-16 relative z-20 animate-slide-up delay-300" data-id="element-871">
        <div className="max-w-4xl mx-auto shadow-xl rounded-lg overflow-hidden" data-id="element-872">
          <WarningBanner message={t('warning.global')} dismissible={false} variant="warning" data-id="element-873" />
        </div>
      </div>

      {/* Quick Actions - Clean Cards */}
      <section className="py-20 sm:py-24 bg-white relative" data-id="element-874">
        {/* Subtle background decoration */}
        <div className="absolute top-0 left-0 w-full h-64 bg-slate-50 skew-y-1 transform origin-top-left -z-10" data-id="element-875"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-876">
          <div className="max-w-6xl mx-auto" data-id="element-877">
            <div className="text-center mb-16" data-id="element-878">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-primary-900" data-id="element-879">
                {t('home.responseSystem')}
              </h2>
              <div className="h-1 w-20 bg-accent-600 mx-auto mb-4 rounded-full" data-id="element-880"></div>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-881">
                {t('home.responseSystemDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-882">
              <Card className="group hover:-translate-y-1 transition-all duration-300 border-t-4 border-t-accent-600 h-full flex flex-col" data-id="element-883">
                <div className="h-14 w-14 bg-accent-50 rounded-xl flex items-center justify-center mb-6 text-accent-600 group-hover:bg-accent-600 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-884">
                  <Search className="h-7 w-7" data-id="element-885" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-primary-900 group-hover:text-accent-600 transition-colors" data-id="element-886">
                  {t('nav.checkScam')}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed flex-grow" data-id="element-887">
                  {t('quickAction.check.desc')}
                </p>
                <Link to={`/${language}/check-scam`} className="inline-flex items-center font-medium text-accent-600 hover:text-accent-700 transition-colors mt-auto" data-id="element-888">
                  {t('action.startAssessment')}{' '}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-889" />
                </Link>
              </Card>

              <Card className="group hover:-translate-y-1 transition-all duration-300 border-t-4 border-t-primary-900 h-full flex flex-col" data-id="element-890">
                <div className="h-14 w-14 bg-primary-50 rounded-xl flex items-center justify-center mb-6 text-primary-900 group-hover:bg-primary-900 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-891">
                  <ShieldCheck className="h-7 w-7" data-id="element-892" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-primary-900 group-hover:text-primary-800 transition-colors" data-id="element-893">
                  {t('nav.emergency')}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed flex-grow" data-id="element-894">
                  {t('quickAction.emergency.desc')}
                </p>
                <Link to={`/${language}/emergency-protocol`} className="inline-flex items-center font-medium text-primary-700 hover:text-primary-900 transition-colors mt-auto" data-id="element-895">
                  {t('action.startProtocol')}{' '}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-896" />
                </Link>
              </Card>

              <Card className="group hover:-translate-y-1 transition-all duration-300 border-t-4 border-t-gold-500 h-full flex flex-col" data-id="element-897">
                <div className="h-14 w-14 bg-gold-50 rounded-xl flex items-center justify-center mb-6 text-gold-600 group-hover:bg-gold-500 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-898">
                  <PhoneCall className="h-7 w-7" data-id="element-899" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-primary-900 group-hover:text-gold-600 transition-colors" data-id="element-900">
                  {t('nav.report')}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed flex-grow" data-id="element-901">
                  {t('quickAction.report.desc')}
                </p>
                <Link to={`/${language}/where-to-report`} className="inline-flex items-center font-medium text-gold-600 hover:text-gold-700 transition-colors mt-auto" data-id="element-902">
                  {t('action.findAgency')}{' '}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-903" />
                </Link>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Protocols Grid - Muted Background */}
      <section className="py-24 bg-slate-50 border-y border-slate-200" data-id="element-904">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-905">
          <div className="max-w-6xl mx-auto" data-id="element-906">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6" data-id="element-907">
              <div className="max-w-2xl" data-id="element-908">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-primary-900" data-id="element-909">
                  {t('home.defenseProtocols')}
                </h2>
                <p className="text-lg text-slate-600" data-id="element-910">
                  {t('home.defenseProtocolsDesc')}
                </p>
              </div>
              <Button href={`/${language}/education`} variant="outline" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-912" />} className="border-primary-200 text-primary-700 hover:bg-primary-50" data-id="element-911">
                {t('home.viewAllProtocols')}
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-913">
              {scamProtocols.map(protocol => <div key={protocol.id} className="animate-fade-in" data-id="element-914">
                  <ProtocolCard protocol={protocol} data-id="element-915" />
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Cyber Quiz CTA Section */}
      <section className="py-20 sm:py-24 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 relative overflow-hidden" data-id="element-916">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10" data-id="element-917">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full animate-pulse" data-id="element-918"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 border border-white/10 rounded-full animate-pulse delay-300" data-id="element-919"></div>
          <div className="absolute top-1/2 left-1/4 w-24 h-24 border border-white/15 rounded-full animate-pulse delay-500" data-id="element-920"></div>
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" data-id="element-921"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-922">
          <div className="max-w-4xl mx-auto text-center" data-id="element-923">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-sm font-medium text-white mb-8 backdrop-blur-sm" data-id="element-924">
              <Brain className="w-4 h-4 mr-2 text-accent-400" data-id="element-925" />
              {t('quiz.badge')}
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-8" data-id="element-926">
              <div className="relative" data-id="element-927">
                <div className="h-20 w-20 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center shadow-xl shadow-accent-500/30 transform rotate-3 hover:rotate-0 transition-transform duration-300" data-id="element-928">
                  <Zap className="h-10 w-10 text-white" data-id="element-929" />
                </div>
                <div className="absolute -top-2 -right-2 h-8 w-8 bg-gold-500 rounded-full flex items-center justify-center shadow-lg" data-id="element-930">
                  <Trophy className="h-4 w-4 text-white" data-id="element-931" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight" data-id="element-932">
              {t('quiz.title')}
            </h2>

            {/* Description */}
            <p className="text-lg sm:text-xl text-primary-100 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-933">
              {t('quiz.description')}
            </p>

            {/* Category badges preview */}
            <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-934">
              <span className="px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm bg-red-500/20 text-red-200 border border-red-400/30" data-id="element-935">
                Digital Noob
              </span>
              <span className="px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm bg-yellow-500/20 text-yellow-200 border border-yellow-400/30" data-id="element-936">
                Cyber Aware
              </span>
              <span className="px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm bg-blue-500/20 text-blue-200 border border-blue-400/30" data-id="element-937">
                Security Pro
              </span>
              <span className="px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm bg-green-500/20 text-green-200 border border-green-400/30" data-id="element-938">
                Firewall Legend
              </span>
            </div>

            {/* CTA Button */}
            <Button href={`/${language}/cyber-quiz`} variant="secondary" size="xl" className="shadow-xl shadow-white/10 hover:shadow-white/20 transition-shadow" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-940" />} data-id="element-939">
              {t('quiz.startBtn')}
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Section - White Background */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-941">
        {/* Subtle star pattern in background */}
        <div className="absolute top-0 right-0 w-1/3 h-full pattern-stars-sm opacity-5 pointer-events-none" data-id="element-942"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-943">
          <div className="max-w-5xl mx-auto text-center" data-id="element-944">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-8 border border-primary-100 shadow-sm" data-id="element-945">
              <Globe className="h-8 w-8 text-primary-900" data-id="element-946" />
            </div>

            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 tracking-tight text-primary-900" data-id="element-947">
              {t('home.internationalStandards')}
            </h3>

            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-948">
              {t('home.internationalStandardsDesc')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-949">
              <Button href={`/${language}/international-framework`} variant="patriot" size="lg" className="w-full sm:w-auto" data-id="element-950">
                {t('home.exploreFramework')}
              </Button>
              <Button href={`/${language}/about`} variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-951">
                {t('home.aboutMission')}
              </Button>
            </div>

            {/* Trust Indicators - Minimal */}
            <div className="mt-16 pt-8 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-952">
              <div className="flex flex-col items-center group" data-id="element-953">
                <div className="p-3 bg-primary-50 rounded-full mb-3 group-hover:bg-primary-100 transition-colors" data-id="element-954">
                  <Shield className="w-6 h-6 text-primary-900" data-id="element-955" />
                </div>
                <span className="text-sm font-semibold text-primary-900" data-id="element-956">
                  {t('home.verifiedProtocols')}
                </span>
              </div>
              <div className="flex flex-col items-center group" data-id="element-957">
                <div className="p-3 bg-primary-50 rounded-full mb-3 group-hover:bg-primary-100 transition-colors" data-id="element-958">
                  <Lock className="w-6 h-6 text-primary-900" data-id="element-959" />
                </div>
                <span className="text-sm font-semibold text-primary-900" data-id="element-960">
                  {t('home.securePlatform')}
                </span>
              </div>
              <div className="flex flex-col items-center group" data-id="element-961">
                <div className="p-3 bg-primary-50 rounded-full mb-3 group-hover:bg-primary-100 transition-colors" data-id="element-962">
                  <Globe className="w-6 h-6 text-primary-900" data-id="element-963" />
                </div>
                <span className="text-sm font-semibold text-primary-900" data-id="element-964">
                  {t('home.aseanCoverage')}
                </span>
              </div>
              <div className="flex flex-col items-center group" data-id="element-965">
                <div className="p-3 bg-primary-50 rounded-full mb-3 group-hover:bg-primary-100 transition-colors" data-id="element-966">
                  <Star className="w-6 h-6 text-primary-900" data-id="element-967" />
                </div>
                <span className="text-sm font-semibold text-primary-900" data-id="element-968">
                  {t('home.officialStandards')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}